/**
 * Custodia de las credenciales de sesión.
 *
 * El *access token* vive SÓLO en memoria: si un XSS logra ejecutar código,
 * al menos no encuentra el token de acceso en disco. El *refresh token* sí se
 * persiste, porque sin él cerrar y abrir la pestaña obligaría a iniciar sesión
 * cada vez; a cambio es de un solo uso y el servidor lo rota en cada refresco.
 */

const REFRESH_KEY = 'crm.refreshToken';

let accessToken: string | null = null;
/** Instante (epoch ms) en el que caduca el token de acceso. */
let accessTokenExpiresAt = 0;

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener();
}

export const tokenStore = {
  getAccessToken(): string | null {
    return accessToken;
  },

  /**
   * `true` cuando al token le queda menos de un margen de seguridad. Se refresca
   * antes de que caduque para no gastar un viaje de ida y vuelta en un 401.
   */
  isAccessTokenNearExpiry(marginSeconds = 60): boolean {
    if (!accessToken) return true;
    return Date.now() >= accessTokenExpiresAt - marginSeconds * 1000;
  },

  getRefreshToken(): string | null {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },

  setSession(access: string, refresh: string, expiresInSeconds: number): void {
    accessToken = access;
    accessTokenExpiresAt = Date.now() + expiresInSeconds * 1000;
    try {
      localStorage.setItem(REFRESH_KEY, refresh);
    } catch {
      // Sin persistencia la sesión dura lo que dure la pestaña.
    }
    notify();
  },

  clear(): void {
    accessToken = null;
    accessTokenExpiresAt = 0;
    try {
      localStorage.removeItem(REFRESH_KEY);
    } catch {
      // Nada que hacer: la memoria ya está limpia.
    }
    notify();
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
